import {compact} from 'lodash';
import React, {FC, ReactNode, useMemo, useRef, useState} from 'react';

import {findDefaultTeamStatusForTicketStatusCategory} from '../../../../../../libs/inboxes/business/src/ticketing/teamStatus';
import {
  computeIsTicketStatusAllowed,
  useIndicatedTicketStatus,
} from '../../../../../../libs/inboxes/business/src/ticketing/ticketStatus';
import {
  getAvailableTicketStatuses,
  getDefaultOpenTicketStatus,
  getWaitingTicketStatuses,
} from '../../../../../../libs/inboxes/store/src/ticketing/ticketStatusSelectors';
import {TicketStatusName} from '../../../../../../libs/inboxes/ui/src/ticketing/ticketStatusName';
import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useEventHandler} from '../../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {FrontEvent, isKeyboardEvent} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ConversationTeamStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {SendActionsEnum} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {TicketStatusModel} from '../../../../../../libs/shared/core/src/models/api/ticketStatusModel';
import {logger} from '../../../../../../libs/shared/core/src/services/logger/logger';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {DropdownSeparator} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemChevron} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemChevron';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {
  KeyboardActionEventHandler,
  KeyboardEventHandlers,
} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {startSimplePerformanceInteraction} from '../../../../../../srcCommon/services/performanceMetrics/interactionPerformance';
import {PerformanceInteractionTypesEnum} from '../../../../../../srcCommon/services/performanceMetrics/interactionPerformanceTypes';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {PermissionResult} from '../../../../../helpers/permissionHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {TicketStatusDropdownItemIcon} from '../../../ticketing/ticketStatusDropdownItemIcon';
import {SnoozeTicketStatusDropdown} from '../../../viewer/conversation/actions/snooze/snoozeTicketStatusDropdown';
import {SafetyNetActionsEnum} from '../../../viewer/conversation/safetyNets/safetyNetsContext';
import {useSafetyNetsCallback} from '../../../viewer/conversation/safetyNets/safetyNetsCoordinator';
import {ReminderDropdown} from '../dropdowns/reminderDropdown';
import {SendLaterDropdown} from '../dropdowns/sendLaterDropdown';
import {SendMessage} from './sendToolbarButton';
import {SendToolbarButtonBase} from './sendToolbarButtonBase';

/*
 * Props.
 */

interface SendTicketStatusToolbarButtonProps {
  conversation: ConversationBaseModel | null;
  isDisabled: boolean;
  renderTooltip: () => ReactNode;
  areSendShortcutsDisabled: boolean | undefined;
  hasClosePermission: boolean;
  updateConversationStatusOfflinePermission: PermissionResult;
  onSendClick: SendMessage;
}

/*
 * Component.
 */

enum DropdownsEnum {
  TICKET_STATUS = 'TICKET_STATUS',
  SEND_LATER = 'SEND_LATER',
  SEND_AND_SNOOZE_TICKET_STATUS = 'SEND_AND_SNOOZE_TICKET_STATUS',
  SEND_AND_SNOOZE_REMINDER = 'SEND_AND_SNOOZE_REMINDER',
}

type ActiveDropdownBaseState = {
  dropdown: DropdownsEnum.TICKET_STATUS | DropdownsEnum.SEND_LATER | DropdownsEnum.SEND_AND_SNOOZE_REMINDER;
  openedByKeyboardAction?: boolean;
};

type ActiveDropdownTicketStatusState = {
  dropdown: DropdownsEnum.SEND_AND_SNOOZE_TICKET_STATUS;
  openedByKeyboardAction?: boolean;
  ticketStatuses: ReadonlyArray<TicketStatusModel>;
};

type ActiveDropdownState = ActiveDropdownBaseState | ActiveDropdownTicketStatusState;

const sendLaterLabel = <Trans id="composer-send-later">Send Later</Trans>;
const sendAndSnoozeLabel = <Trans id="composer-send-snooze">Send &amp; Snooze</Trans>;

export const SendTicketStatusToolbarButton: FC<SendTicketStatusToolbarButtonProps> = (props) => {
  const ticketStatuses = useAppSelector(getAvailableTicketStatuses);
  const waitingTicketStatuses = useAppSelector(getWaitingTicketStatuses);

  const conversations = compact([props.conversation]);

  const defaultOpenTicketStatus = useAppSelector(getDefaultOpenTicketStatus);
  const currentTicketStatus = useIndicatedTicketStatus({conversations}) ?? defaultOpenTicketStatus;

  const chromeRef = useRef<HTMLDivElement>(null);
  const [activeDropdown, setActiveDropdown] = useState<ActiveDropdownState | undefined>(undefined);
  const [sendAndSnoozeTicketStatus, setSendAndSnoozeTicketStatus] = useState<TicketStatusModel | undefined>();

  const onOpenTicketStatusSelect = (event: FrontEvent, ticketStatus: TicketStatusModel) => {
    // We can measure performance only when we're not using safety nets.
    startSimplePerformanceInteraction(event, PerformanceInteractionTypesEnum.SEND_MESSAGE);

    props.onSendClick({
      sendAction: SendActionsEnum.SEND,
      ticketStatusTagId: ticketStatus.tagId,
    });
  };

  const onValidArchiveTicketStatusSelect = (ticketStatus: TicketStatusModel) => {
    props.onSendClick({
      sendAction: SendActionsEnum.SEND_AND_ARCHIVE,
      ticketStatusTagId: ticketStatus.tagId,
    });
  };

  const onArchiveTicketStatusSelect = useSafetyNetsCallback({
    safetyNetActionType: SafetyNetActionsEnum.SEND_AND_ARCHIVE,
    conversations,
    onActionValidated: onValidArchiveTicketStatusSelect,
  });

  const onTicketStatusSelect = (event: FrontEvent, ticketStatus: TicketStatusModel) => {
    const teamStatus = findDefaultTeamStatusForTicketStatusCategory(ticketStatus.category);
    if (teamStatus === ConversationTeamStatusesEnum.OPEN) {
      onOpenTicketStatusSelect(event, ticketStatus);
    } else {
      onArchiveTicketStatusSelect(ticketStatus);
    }
  };

  const onSendLaterClick = (event: FrontEvent) => {
    event.preventDefault();
    setActiveDropdown({dropdown: DropdownsEnum.SEND_LATER, openedByKeyboardAction: isKeyboardEvent(event)});
  };

  const onSendLaterValueSelect = (sendLaterAt: number) => {
    props.onSendClick({
      sendLaterAt,
    });

    // Tracking.
    const delay = Math.round((sendLaterAt - Date.now()) / 1000);
    trackInterfaceEvent(InterfaceEventsEnum.MESSAGE_SENT_LATER, {delay});
  };

  const onSendAndSnoozeClick = useEventHandler((event: FrontEvent) => {
    event.preventDefault();
    if (waitingTicketStatuses && waitingTicketStatuses.length > 1) {
      setActiveDropdown({
        dropdown: DropdownsEnum.SEND_AND_SNOOZE_TICKET_STATUS,
        openedByKeyboardAction: isKeyboardEvent(event),
        ticketStatuses: waitingTicketStatuses,
      });
    } else {
      setActiveDropdown({
        dropdown: DropdownsEnum.SEND_AND_SNOOZE_REMINDER,
        openedByKeyboardAction: isKeyboardEvent(event),
      });
    }
  });

  const onSendAndSnoozeTicketStatusSelect = (event: FrontEvent, ticketStatus: TicketStatusModel) => {
    setSendAndSnoozeTicketStatus(ticketStatus);

    event.preventDefault();
    setActiveDropdown({
      dropdown: DropdownsEnum.SEND_AND_SNOOZE_REMINDER,
      openedByKeyboardAction: isKeyboardEvent(event),
    });
  };

  const onSendLaterBackClick = () => {
    setActiveDropdown({
      dropdown: DropdownsEnum.TICKET_STATUS,
      openedByKeyboardAction: false,
    });
  };

  const onSnoozeTicketStatusBackClick = () => {
    setActiveDropdown({
      dropdown: DropdownsEnum.TICKET_STATUS,
    });
  };

  const onSnoozeAndSendBackClick = () => {
    if (waitingTicketStatuses && waitingTicketStatuses.length > 1) {
      setActiveDropdown({
        dropdown: DropdownsEnum.SEND_AND_SNOOZE_TICKET_STATUS,
        ticketStatuses: waitingTicketStatuses,
      });
    } else {
      setActiveDropdown({
        dropdown: DropdownsEnum.TICKET_STATUS,
      });
    }
  };

  const onSendAndSnoozeReminderSelect = (remindAt: number) => {
    props.onSendClick({
      sendAction: SendActionsEnum.SEND_AND_ARCHIVE,
      ticketStatusTagId: sendAndSnoozeTicketStatus?.tagId,
      remindAt,
    });
  };

  const onDefaultButtonClick = useEventHandler((event: FrontEvent) => {
    if (!currentTicketStatus) {
      logger.error('SendTicketStatusToolbarButton#onDefaultButtonClick is not doing anything');
      return;
    }

    onTicketStatusSelect(event, currentTicketStatus);
  });

  const onOptionsButtonClick = useEventHandler((event: FrontEvent) => {
    setActiveDropdown({
      dropdown: DropdownsEnum.TICKET_STATUS,
      openedByKeyboardAction: isKeyboardEvent(event),
    });
  }) satisfies KeyboardActionEventHandler;

  const keyboardHandlers = useMemo<KeyboardEventHandlers>(
    () => ({
      [KeyboardActionsEnum.SEND]: onDefaultButtonClick,
      [KeyboardActionsEnum.SEND_AND_DONE]: onOptionsButtonClick,
      [KeyboardActionsEnum.SEND_AND_SNOOZE]: onSendAndSnoozeClick,
    }),
    [onDefaultButtonClick, onOptionsButtonClick, onSendAndSnoozeClick],
  );

  // This button should only be rendered when ticketing is provisioned.
  // TODO: Render an error state, just in case.
  if (!ticketStatuses || !currentTicketStatus) {
    logger.error('SendTicketStatusToolbarButton is not rendering');
    return null;
  }

  const button = (
    <KeyboardShortcuts
      handlers={keyboardHandlers}
      isDisabled={props.isDisabled || props.areSendShortcutsDisabled}
    >
      <SendToolbarButtonBase
        chromeRef={chromeRef}
        isDisabled={props.isDisabled}
        renderTooltip={props.renderTooltip}
        isDefaultDropdownOpen={false}
        renderDefaultButtonContent={() => renderTicketStatusActionLabel(currentTicketStatus)}
        onDefaultButtonClick={onDefaultButtonClick}
        isOptionsDropdownOpen={activeDropdown !== undefined}
        onOptionsButtonClick={onOptionsButtonClick}
      />
    </KeyboardShortcuts>
  );

  const isTicketStatusAllowed = (newTicketStatus: TicketStatusModel) =>
    computeIsTicketStatusAllowed(currentTicketStatus.tagId, newTicketStatus, {
      isOnlineArchiveAllowed: props.hasClosePermission,
      isUpdateConversationStatusOfflineAllowed: props.updateConversationStatusOfflinePermission.isAllowed,
    });

  const maybeRenderDropdown = () => {
    switch (activeDropdown?.dropdown) {
      case DropdownsEnum.TICKET_STATUS: {
        const defaultHighlightedIndex = ticketStatuses.findIndex(
          (ticketStatus) => ticketStatus.tagId === currentTicketStatus.tagId,
        );

        return (
          <SimpleDropdown
            anchor={chromeRef}
            placement="top-end"
            onRequestClose={() => setActiveDropdown(undefined)}
            defaultHighlightedIndex={
              activeDropdown.openedByKeyboardAction && defaultHighlightedIndex >= 0
                ? 2 + defaultHighlightedIndex // Offset by 2 for send later and send & snooze.
                : undefined
            }
          >
            <DropdownItem onSelect={onSendLaterClick}>
              <DropdownItemIcon iconName="timeSmall" iconSize={16} label={sendLaterLabel} />
              <DropdownItemContent>{sendLaterLabel}</DropdownItemContent>
              <DropdownItemChevron />
            </DropdownItem>

            <DropdownItem onSelect={onSendAndSnoozeClick}>
              <DropdownItemIcon iconName="snoozeSmall" iconSize={16} label={sendAndSnoozeLabel} />
              <DropdownItemContent>{sendAndSnoozeLabel}</DropdownItemContent>
              <DropdownItemChevron />
            </DropdownItem>

            <DropdownSeparator />

            {ticketStatuses.map((ticketStatus) => (
              <DropdownItem
                key={ticketStatus.tagId}
                onSelect={(event) => onTicketStatusSelect(event, ticketStatus)}
                isDisabled={!isTicketStatusAllowed(ticketStatus)}
              >
                <TicketStatusDropdownItemIcon category={ticketStatus.category} />
                <DropdownItemContent>{renderTicketStatusActionLabel(ticketStatus)}</DropdownItemContent>
              </DropdownItem>
            ))}
          </SimpleDropdown>
        );
      }

      case DropdownsEnum.SEND_LATER:
        return (
          <SendLaterDropdown
            anchor={chromeRef}
            placement="top-end"
            onRequestClose={() => setActiveDropdown(undefined)}
            onValueSelect={onSendLaterValueSelect}
            onBackClick={onSendLaterBackClick}
            backLabel={<Trans id="send-ticket-status-dropdown-back">Back</Trans>}
          />
        );

      case DropdownsEnum.SEND_AND_SNOOZE_TICKET_STATUS: {
        return (
          <SnoozeTicketStatusDropdown
            anchor={chromeRef}
            placement="top-end"
            onRequestClose={() => setActiveDropdown(undefined)}
            title={sendAndSnoozeLabel}
            computeIsTicketStatusDisabled={(ticketStatus) => !isTicketStatusAllowed(ticketStatus)}
            ticketStatuses={activeDropdown.ticketStatuses}
            onTicketStatusSelect={onSendAndSnoozeTicketStatusSelect}
            onBackClick={onSnoozeTicketStatusBackClick}
            backLabel={<Trans id="send-ticket-status-dropdown-back">Back</Trans>}
          />
        );
      }

      case DropdownsEnum.SEND_AND_SNOOZE_REMINDER:
        return (
          <ReminderDropdown
            anchor={chromeRef}
            placement="top-end"
            onRequestClose={() => setActiveDropdown(undefined)}
            onValueSelect={onSendAndSnoozeReminderSelect}
            onBackClick={onSnoozeAndSendBackClick}
            backLabel={<Trans id="send-ticket-status-dropdown-back">Back</Trans>}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {button}
      {maybeRenderDropdown()}
    </>
  );
};

function renderTicketStatusActionLabel(ticketStatus: TicketStatusModel) {
  return (
    <Trans id="composer-send-ticketstatus">
      Send as <TicketStatusName tagId={ticketStatus.tagId} />
    </Trans>
  );
}
